import { LitElement, html, css } from 'lit';
import { money as fmtMoney } from '../../format';
import { t } from '../../i18n';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../../api';
import { store } from '../../store';
import { router } from '../../router';
import { showToast } from '../../components/ui/toast';
import { moduleStudentTabs } from '../../modules/registry';

@customElement('cadence-student-detail-page')
export class CadenceStudentDetailPage extends LitElement {
  @property() studentId = '';
  @state() private s: any = null;
  @state() private tab = 'profile';
  @state() private loading = true;
  @state() private danceTypes: any[] = [];
  @state() private levels: any[] = [];
  @state() private classes: any[] = [];
  @state() private allTags: any[] = [];
  @state() private levelForm = { dance_type_id: '', level_id: '' };
  @state() private enrollClassId = '';
  @state() private tagToAdd = '';
  @state() private attendance: any[] = [];
  @state() private resetLink = '';
  // Billing (payments tab)
  @state() private payments: any[] = [];
  @state() private studentFees: any[] = [];
  @state() private tier: any = null;
  @state() private feeCatalog: any[] = [];
  @state() private tiers: any[] = [];
  @state() private assignFeeId = '';
  @state() private methods: any[] = [];
  // Record-payment dialog state
  @state() private recordingId = '';
  @state() private recordMethodId = '';
  @state() private recordTxn = '';
  @state() private recordNotes = '';
  @state() private editOverrideId = '';        // student_fee id being edited
  @state() private overrideAmount = '';
  @state() private overrideNote = '';
  @state() private creditBalance = 0;
  @state() private creditAmount = '';
  @state() private creditNote = '';
  @state() private credits: any[] = [];
  @state() private showCreditDetails = false;
  // GDPR (data & privacy)
  @state() private exporting = false;
  @state() private erasing = false;
  @state() private showErase = false;
  @state() private eraseConfirm = '';

  static styles = css`
    :host { display: block; }
    .back { color: var(--color-primary); text-decoration: none; font-size: 0.88rem; }
    .header { display: flex; align-items: center; gap: 16px; margin: var(--space-3) 0 var(--space-4); }
    .header h1 { margin: 0; font-size: 1.5rem; }
    .meta { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-top: 4px; }
    .tabwrap { margin-top: var(--space-4); }
    .panel { padding-top: var(--space-4); display: grid; gap: var(--space-4); }
    .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: var(--space-4); }
    .field { font-size: 0.92rem; }
    .field .k { color: var(--color-text-secondary); font-size: 0.8rem; }
    .field .v { font-weight: 600; }
    .row { display: flex; gap: 10px; align-items: flex-end; flex-wrap: wrap; }
    .tags { display: flex; gap: 6px; flex-wrap: wrap; align-items: center; }
    .tag-rm { cursor: pointer; margin-left: 2px; }
    .banner { margin: 0 0 var(--space-3); padding: 10px 14px; border: 1px solid var(--color-primary); border-radius: var(--radius-md); background: color-mix(in srgb, var(--color-primary) 8%, transparent); font-size: 0.88rem; word-break: break-all; }
    .banner a { color: var(--color-primary); }
    .rm { cursor: pointer; color: var(--color-danger); background: none; border: none; font: inherit; font-size: 0.85rem; }
    .muted { color: var(--color-text-secondary); font-size: 0.8rem; }
    .linkbtn { background: none; border: none; padding: 0; font: inherit; color: var(--color-primary); cursor: pointer; }
    .linkbtn:hover { text-decoration: underline; }
    .override { margin-top: 12px; padding: 14px; border: 1px solid var(--color-primary); border-radius: var(--radius-md); background: color-mix(in srgb, var(--color-primary) 6%, transparent); }
    .override-head { margin-bottom: 10px; font-size: 0.92rem; }
    .gdpr { display: block; margin-top: var(--space-5); }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    this.loadAll();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('studentId')) this.loadAll();
  }

  private async loadAll() {
    if (!this.studentId) return;
    this.loading = true;
    try {
      const [s, types, levels, classes, tags] = await Promise.all([
        api.get<any>(`/students/${this.studentId}`),
        api.get<any[]>('/dance/types'),
        api.get<any[]>('/dance/levels'),
        api.get<any[]>('/classes'),
        api.get<any[]>('/tags'),
      ]);
      this.s = s; this.danceTypes = types; this.levels = levels; this.classes = classes; this.allTags = tags;
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      this.loading = false;
    }
  }

  private async reload() { this.s = await api.get<any>(`/students/${this.studentId}`); }

  private async loadAttendance() {
    try { this.attendance = await api.get<any[]>(`/students/${this.studentId}/attendance`); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }

  private async resetPassword() {
    try {
      const { onboarding_token } = await api.post<{ onboarding_token: string }>(`/students/${this.studentId}/reset-password`);
      this.resetLink = `${window.location.origin}/onboarding/${onboarding_token}`;
      showToast(t('students.toast.linkRegenerated'), 'success');
      await this.reload();
    } catch (e) { showToast((e as Error).message, 'error'); }
  }

  private async exportData() {
    this.exporting = true;
    try {
      await api.download(`/students/${this.studentId}/export`, 'gdpr-export.zip');
      showToast(t('students.detail.gdpr.exportStarted'), 'success');
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.exporting = false; }
  }

  private async eraseData() {
    this.erasing = true;
    try {
      await api.del(`/students/${this.studentId}/data`, { confirm: this.eraseConfirm });
      showToast(t('students.detail.gdpr.erased'), 'success');
      this.showErase = false; this.eraseConfirm = '';
      await this.reload();
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.erasing = false; }
  }

  private selectTab(tab: string) {
    this.tab = tab;
    if (tab === 'attendance') this.loadAttendance();
    if (tab === 'payments') this.loadBilling();
  }

  private async loadBilling() {
    try {
      const [feesRes, payRes, catalog, tiers, creditsRes, methods] = await Promise.all([
        api.get<{ fees: any[]; tier: any; credit_balance: number }>(`/students/${this.studentId}/fees`),
        api.get<{ payments: any[] }>(`/payments?student_id=${this.studentId}&limit=100`),
        api.get<any[]>('/fees'),
        api.get<any[]>('/fees/tiers'),
        api.get<{ balance: number; credits: any[] }>(`/students/${this.studentId}/credits`),
        api.get<any[]>('/fees/methods'),
      ]);
      this.studentFees = feesRes.fees; this.tier = feesRes.tier; this.creditBalance = creditsRes.balance ?? 0;
      this.payments = payRes.payments; this.feeCatalog = catalog; this.tiers = tiers; this.credits = creditsRes.credits;
      this.methods = methods;
    } catch (e) { showToast((e as Error).message, 'error'); }
  }

  private async setTier(tierId: string) {
    try {
      await api.patch(`/students/${this.studentId}/price-tier`, { price_tier_id: tierId || null });
      showToast(t('students.toast.tierUpdated'), 'success');
      await this.loadBilling();
    } catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async assignFee() {
    if (!this.assignFeeId) return;
    try {
      await api.post(`/fees/${this.assignFeeId}/assign`, { student_ids: [this.studentId] });
      this.assignFeeId = '';
      await this.loadBilling();
    } catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async unassignFee(sfId: string) { await api.del(`/fees/assignments/${sfId}`); await this.loadBilling(); }

  private startOverride(sf: any) {
    this.editOverrideId = sf.id;
    this.overrideAmount = sf.override_amount != null ? String(sf.override_amount) : '';
    this.overrideNote = sf.override_note ?? '';
  }
  private cancelOverride() { this.editOverrideId = ''; this.overrideAmount = ''; this.overrideNote = ''; }
  private async saveOverride(sfId: string) {
    // Blank amount clears the override (revert to tier-resolved price).
    const raw = this.overrideAmount.trim();
    const override_amount = raw === '' ? null : Number(raw);
    if (override_amount != null && (!Number.isFinite(override_amount) || override_amount < 0)) {
      showToast(t('students.toast.invalidOverride'), 'error'); return;
    }
    try {
      await api.put(`/fees/assignments/${sfId}`, { override_amount, override_note: this.overrideNote.trim() || null });
      showToast(override_amount == null ? t('students.toast.overrideCleared') : t('students.toast.overrideSaved'), 'success');
      this.cancelOverride();
      await this.loadBilling();
    } catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async generateMonth() {
    try { const r = await api.post<{ created: number }>('/payments/generate', {}); showToast(t('students.toast.paymentsGenerated', { count: r.created }), 'success'); await this.loadBilling(); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }
  /** Open the record dialog, pre-selecting the default (enabled) method. */
  private openRecord(id: string) {
    const enabled = this.methods.filter((m) => m.enabled);
    const def = enabled.find((m) => m.is_default) ?? enabled[0];
    this.recordingId = id;
    this.recordMethodId = def?.id ?? '';
    this.recordTxn = '';
    this.recordNotes = '';
  }
  private closeRecord() { this.recordingId = ''; }
  private async confirmRecord() {
    if (!this.recordMethodId) { showToast(t('students.toast.chooseMethod'), 'error'); return; }
    try {
      await api.post(`/payments/${this.recordingId}/record`, {
        payment_method_id: this.recordMethodId,
        transaction_id: this.recordTxn.trim() || null,
        notes: this.recordNotes.trim() || null,
      });
      showToast(t('students.toast.paymentRecorded'), 'success');
      this.closeRecord();
      await this.loadBilling();
    } catch (e) { showToast((e as Error).message, 'error'); }
  }
  /** Combined credit history: issuances (added) + applications to payments (used), newest first. */
  private creditActivity() {
    const added = this.credits.map((c) => ({
      date: c.created_at, kind: 'added' as const, detail: c.note || t('students.toast.creditIssued'),
      amount: Number(c.amount), currency: this.payments[0]?.currency || this.studentFees[0]?.currency || 'USD',
    }));
    const used = this.payments
      .filter((p) => Number(p.credit_applied) > 0)
      .map((p) => ({
        date: p.paid_at || p.created_at, kind: 'used' as const,
        detail: p.fee_name || t('students.detail.credit.paymentDefault'), amount: Number(p.credit_applied), currency: p.currency,
      }));
    return [...added, ...used].sort((a, b) => (a.date < b.date ? 1 : -1));
  }

  private async issueCredit() {
    const amount = Number(this.creditAmount);
    if (!amount || amount <= 0) { showToast(t('students.toast.invalidCredit'), 'error'); return; }
    try {
      await api.post(`/students/${this.studentId}/credits`, { amount, note: this.creditNote || null });
      this.creditAmount = ''; this.creditNote = '';
      showToast(t('students.toast.creditIssued'), 'success');
      await this.loadBilling();
    } catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async cancelPay(id: string) {
    try { await api.post(`/payments/${id}/cancel`, {}); await this.loadBilling(); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }
  private money(amount: number, currency: string) { return fmtMoney(amount, currency); }
  private payColor(status: string) {
    return status === 'paid' ? 'var(--color-success)'
      : status === 'overdue' ? 'var(--color-danger)'
      : status === 'cancelled' ? 'var(--color-text-secondary)' : 'var(--color-primary)';
  }

  private async assignLevel() {
    if (!this.levelForm.dance_type_id || !this.levelForm.level_id) return;
    await api.post(`/students/${this.studentId}/levels`, this.levelForm);
    showToast(t('students.toast.levelUpdated'), 'success');
    this.levelForm = { dance_type_id: '', level_id: '' };
    await this.reload();
  }

  private async enroll() {
    if (!this.enrollClassId) return;
    try {
      await api.post(`/classes/${this.enrollClassId}/enroll`, { student_id: this.studentId });
      showToast(t('students.toast.enrolled'), 'success');
      this.enrollClassId = '';
      await this.reload();
    } catch (e) { showToast((e as Error).message, 'error'); }
  }

  private async addTag() {
    if (!this.tagToAdd) return;
    await api.post(`/students/${this.studentId}/tags`, { tag_id: this.tagToAdd });
    this.tagToAdd = '';
    await this.reload();
  }
  private async removeTag(tagId: string) {
    await api.del(`/students/${this.studentId}/tags/${tagId}`);
    await this.reload();
  }
  private async toggleStatus() {
    const next = this.s.status === 'active' ? 'inactive' : 'active';
    await api.patch(`/students/${this.studentId}/status`, { status: next, status_notes: `Toggled to ${next}` });
    await this.reload();
  }

  private field(label: string, value: unknown) {
    return html`<div class="field"><div class="k">${label}</div><div class="v">${value || '—'}</div></div>`;
  }

  /** Inline editor for a per-student fee override (a custom price for this student,
   *  overriding the tier-resolved amount). Blank amount clears it. */
  private renderOverrideEditor() {
    const sf = this.studentFees.find((f) => f.id === this.editOverrideId);
    if (!sf) return '';
    return html`
      <div class="override">
        <div class="override-head">${t('students.detail.override.customPrice')} <strong>${sf.fee_name}</strong> <span class="muted">${t('students.detail.override.default', { amount: this.money(sf.base_amount ?? 0, sf.currency) })}</span></div>
        <div class="row" style="gap:10px;align-items:flex-end;flex-wrap:wrap">
          <cadence-input label=${t('students.detail.override.amountLabel', { currency: sf.currency })} type="number" placeholder=${t('students.detail.override.amountPlaceholder')}
            .value=${this.overrideAmount} @input=${(e: CustomEvent) => (this.overrideAmount = e.detail)}></cadence-input>
          <cadence-input label=${t('students.detail.override.noteLabel')} placeholder=${t('students.detail.override.notePlaceholder')}
            .value=${this.overrideNote} @input=${(e: CustomEvent) => (this.overrideNote = e.detail)}></cadence-input>
          <cadence-button @click=${() => this.saveOverride(sf.id)}>${t('common.actions.save')}</cadence-button>
          <cadence-button variant="ghost" @click=${() => this.cancelOverride()}>${t('common.actions.cancel')}</cadence-button>
        </div>
        <p class="muted" style="margin:8px 0 0">${t('students.detail.override.clearHint')}</p>
      </div>`;
  }

  private attColor(status: string) {
    return status === 'attending' ? 'var(--color-success)'
      : status === 'absent' ? 'var(--color-danger)'
      : status === 'not_attending' ? 'var(--color-text-secondary)' : 'var(--color-primary)';
  }

  render() {
    if (this.loading || !this.s) return html`<cadence-loading></cadence-loading>`;
    const s = this.s;
    const moduleTabs = moduleStudentTabs();
    const tabs = [
      { id: 'profile', label: t('students.tabs.profile') }, { id: 'classes', label: t('students.tabs.classes') }, { id: 'levels', label: t('students.tabs.levels') },
      { id: 'payments', label: t('students.tabs.payments') }, { id: 'attendance', label: t('students.tabs.attendance') },
      ...moduleTabs.map((mt) => ({ id: mt.id, label: t(mt.labelKey) })),
    ];
    return html`
      <a class="back" href="/admin/students" data-link>← ${t('students.list.title')}</a>
      <div class="header">
        <cadence-avatar name=${`${s.first_name} ${s.last_name}`} src=${s.profile_photo_url ?? ''} size="56"></cadence-avatar>
        <div>
          <h1>${s.first_name} ${s.last_name}</h1>
          <div class="meta">
            <cadence-badge variant="status" color=${s.status === 'active' ? 'var(--color-success)' : 'var(--color-text-secondary)'}>${s.status}</cadence-badge>
            ${s.anonymized_at ? html`<cadence-badge color="var(--color-danger)">${t('students.detail.gdpr.badge')}</cadence-badge>` : ''}
            <div class="tags">
              ${(s.tags ?? []).map((t: any) => html`<cadence-badge color=${t.color}>${t.name} <span class="tag-rm" @click=${() => this.removeTag(t.id)}>×</span></cadence-badge>`)}
            </div>
          </div>
        </div>
      </div>

      <div class="row" style="margin-bottom:8px">
        <cadence-select placeholder=${t('students.detail.addTagPlaceholder')} .value=${this.tagToAdd}
          .options=${this.allTags.filter((tag) => !(s.tags ?? []).some((x: any) => x.id === tag.id)).map((tag) => ({ value: tag.id, label: tag.name }))}
          @change=${(e: CustomEvent) => { this.tagToAdd = e.detail; this.addTag(); }}></cadence-select>
        <cadence-button variant="secondary" @click=${() => this.toggleStatus()}>
          ${s.status === 'active' ? t('students.detail.deactivate') : t('students.detail.activate')}
        </cadence-button>
        <cadence-button variant="ghost" @click=${() => this.resetPassword()}>${t('students.detail.resetPassword')}</cadence-button>
      </div>

      ${this.resetLink ? html`<div class="banner">${t('students.detail.onboardingLink')} <a href=${this.resetLink}>${this.resetLink}</a></div>` : ''}

      <div class="tabwrap">
        <cadence-tabs .tabs=${tabs} active=${this.tab} @tab-change=${(e: CustomEvent) => this.selectTab(e.detail)}></cadence-tabs>

        ${this.tab === 'profile' ? html`
          <div class="panel"><div class="grid">
            ${this.field(t('students.detail.fields.email'), s.email)}
            ${this.field(t('students.detail.fields.phone'), s.phone)}
            ${this.field(t('students.detail.fields.dob'), s.date_of_birth)}
            ${this.field(t('students.detail.fields.gender'), s.gender)}
            ${this.field(t('students.detail.fields.address'), s.address)}
            ${this.field(t('students.detail.fields.guardian'), s.guardian_name)}
            ${this.field(t('students.detail.fields.statusNotes'), s.status_notes)}
            ${this.field(t('students.detail.fields.contractAccepted'), s.contract_accepted ? t('students.detail.yes') : t('students.detail.no'))}
          </div></div>` : ''}

        ${this.tab === 'classes' ? html`
          <div class="panel">
            <div class="row">
              <cadence-select label=${t('students.detail.classes.enrollLabel')} placeholder=${t('students.detail.classes.selectPlaceholder')} .value=${this.enrollClassId}
                .options=${this.classes.filter((c) => !(s.enrolled_classes ?? []).some((e: any) => e.id === c.id)).map((c) => ({ value: c.id, label: c.name }))}
                @change=${(e: CustomEvent) => (this.enrollClassId = e.detail)}></cadence-select>
              <cadence-button @click=${() => this.enroll()}>${t('students.detail.classes.enroll')}</cadence-button>
            </div>
            ${(s.enrolled_classes ?? []).length
              ? html`<cadence-table .columns=${[
                  { key: 'name', label: t('students.detail.classes.colClass') }, { key: 'teacher_name', label: t('students.detail.classes.colTeacher') },
                  { key: 'day_of_week', label: t('students.detail.classes.colDay') }, { key: 'start_time', label: t('students.detail.classes.colTime') },
                ]} .rows=${s.enrolled_classes}></cadence-table>`
              : html`<cadence-empty-state heading=${t('students.detail.classes.empty')}></cadence-empty-state>`}
          </div>` : ''}

        ${this.tab === 'levels' ? html`
          <div class="panel">
            <div class="row">
              <cadence-select label=${t('students.detail.levels.levelLabel')} placeholder=${t('students.detail.levels.levelPlaceholder')} .value=${this.levelForm.level_id}
                .options=${this.levels.map((l) => ({ value: l.id, label: l.name }))}
                @change=${(e: CustomEvent) => (this.levelForm = { ...this.levelForm, level_id: e.detail })}></cadence-select>
              <cadence-select label=${t('students.detail.levels.danceTypeLabel')} placeholder=${t('students.detail.levels.danceTypePlaceholder')} .value=${this.levelForm.dance_type_id}
                .options=${this.danceTypes.map((d) => ({ value: d.id, label: d.name }))}
                @change=${(e: CustomEvent) => (this.levelForm = { ...this.levelForm, dance_type_id: e.detail })}></cadence-select>
              <cadence-button @click=${() => this.assignLevel()}>${t('students.detail.levels.assignUpdate')}</cadence-button>
            </div>
            ${(s.dance_levels ?? []).length
              ? html`<cadence-table .columns=${[
                  { key: 'level_name', label: t('students.detail.levels.levelLabel') }, { key: 'dance_type_name', label: t('students.detail.levels.danceTypeLabel') },
                ]} .rows=${s.dance_levels}></cadence-table>`
              : html`<cadence-empty-state heading=${t('students.detail.levels.empty')}></cadence-empty-state>`}
          </div>` : ''}

        ${this.tab === 'payments' ? html`
          <div class="panel">
            <cadence-card heading=${t('students.detail.payments.pricingFees')}>
              <div class="row" style="margin-bottom:12px">
                <cadence-select label=${t('students.detail.payments.priceTier')} .value=${this.tier?.id ?? ''}
                  .options=${this.tiers.map((tier) => ({ value: tier.id, label: `${tier.name}${tier.adjustment_value ? ` (${tier.adjustment_type === 'percent' ? `${tier.adjustment_value}%` : tier.adjustment_value}) ` : ''}` }))}
                  @change=${(e: CustomEvent) => this.setTier(e.detail)}></cadence-select>
                <cadence-select label=${t('students.detail.payments.assignFee')} placeholder=${t('students.detail.payments.chooseFee')} .value=${this.assignFeeId}
                  .options=${this.feeCatalog.filter((f) => !this.studentFees.some((sf) => sf.fee_definition_id === f.id)).map((f) => ({ value: f.id, label: `${f.name} · ${this.money(f.base_amount, f.currency)} (${f.type})` }))}
                  @change=${(e: CustomEvent) => { this.assignFeeId = e.detail; this.assignFee(); }}></cadence-select>
              </div>
              ${this.studentFees.length
                ? html`<cadence-table .columns=${[
                    { key: 'fee_name', label: t('students.detail.payments.colFee') },
                    { key: 'type', label: t('students.detail.payments.colType') },
                    { key: 'resolved_amount', label: t('students.detail.payments.colAmount'), render: (r: any) => html`${this.money(r.resolved_amount, r.currency)}${r.override_amount != null ? html` <span class="muted">${t('students.detail.payments.overrideTag')}</span>` : ''}` },
                    { key: 'id', label: '', render: (r: any) => html`<div style="display:flex;gap:6px;justify-content:flex-end"><cadence-button variant="ghost" @click=${() => this.startOverride(r)}>${t('students.detail.payments.override')}</cadence-button><cadence-button variant="danger" @click=${() => this.unassignFee(r.id)}>${t('students.detail.payments.remove')}</cadence-button></div>` },
                  ]} .rows=${this.studentFees}></cadence-table>
                  ${this.editOverrideId ? this.renderOverrideEditor() : ''}`
                : html`<cadence-empty-state heading=${t('students.detail.payments.noFees')}></cadence-empty-state>`}
            </cadence-card>

            <cadence-card heading=${t('students.tabs.payments')}>
              <div class="row" style="margin-bottom:12px">
                <cadence-button variant="secondary" @click=${() => this.generateMonth()}>${t('students.detail.payments.generateMonth')}</cadence-button>
              </div>
              ${this.payments.length
                ? html`<cadence-table .columns=${[
                    { key: 'due_date', label: t('students.detail.payments.colDue') },
                    { key: 'fee_name', label: t('students.detail.payments.colFee'), render: (r: any) => html`${r.fee_name ?? '—'}` },
                    { key: 'amount', label: t('students.detail.payments.colAmount'), render: (r: any) => html`${this.money(r.amount, r.currency)}${r.credit_applied ? html` <span class="muted">${t('students.detail.payments.creditTag', { amount: this.money(r.credit_applied, r.currency) })}</span>` : ''}` },
                    { key: 'method_name', label: t('students.detail.payments.colMethod'), render: (r: any) => html`${r.status === 'paid' ? (r.method_name ?? '—') : html`<span class="muted">—</span>`}` },
                    { key: 'status', label: t('students.detail.payments.colStatus'), render: (r: any) => html`<cadence-badge color=${this.payColor(r.status)}>${r.status}</cadence-badge>` },
                    { key: 'id', label: '', render: (r: any) => r.status === 'paid' || r.status === 'cancelled' ? '' : html`<div style="display:flex;gap:6px;justify-content:flex-end"><cadence-button variant="ghost" @click=${() => this.openRecord(r.id)}>${t('students.detail.payments.record')}</cadence-button><cadence-button variant="danger" @click=${() => this.cancelPay(r.id)}>${t('common.actions.cancel')}</cadence-button></div>` },
                  ]} .rows=${this.payments}></cadence-table>`
                : html`<cadence-empty-state heading=${t('students.detail.payments.noPaymentsHeading')} message=${t('students.detail.payments.noPaymentsMessage')}></cadence-empty-state>`}
            </cadence-card>

            <cadence-card heading=${t('students.detail.credit.title')}>
              <p class="muted" style="margin-top:0">
                ${t('students.detail.credit.balanceLabel')} <strong style="color:${this.creditBalance > 0 ? 'var(--color-success)' : 'inherit'}">${this.money(this.creditBalance, this.payments[0]?.currency || this.studentFees[0]?.currency || 'USD')}</strong> — ${t('students.detail.credit.autoApplied')}
                ${this.creditActivity().length ? html` · <button class="linkbtn" @click=${() => (this.showCreditDetails = !this.showCreditDetails)}>${this.showCreditDetails ? t('students.detail.credit.hideDetails') : t('students.detail.credit.showDetails')}</button>` : ''}
              </p>
              ${this.showCreditDetails && this.creditActivity().length ? html`
                <cadence-table .columns=${[
                  { key: 'date', label: t('students.detail.credit.colDate'), render: (r: any) => html`${(r.date ?? '').slice(0, 10)}` },
                  { key: 'kind', label: t('students.detail.credit.colActivity'), render: (r: any) => html`<cadence-badge color=${r.kind === 'added' ? 'var(--color-success)' : 'var(--color-text-secondary)'}>${r.kind === 'added' ? t('students.detail.credit.added') : t('students.detail.credit.used')}</cadence-badge>` },
                  { key: 'detail', label: t('students.detail.credit.colDetail'), render: (r: any) => html`${r.detail}` },
                  { key: 'amount', label: t('students.detail.credit.colAmount'), render: (r: any) => html`<span style="color:${r.kind === 'added' ? 'var(--color-success)' : 'var(--color-danger)'}">${r.kind === 'added' ? '+' : '−'}${this.money(r.amount, r.currency)}</span>` },
                ]} .rows=${this.creditActivity()}></cadence-table>
              ` : ''}
              <div class="row" style="gap:10px;align-items:flex-end;flex-wrap:wrap;margin-top:12px">
                <cadence-input label=${t('students.detail.credit.issueLabel')} type="number" placeholder=${t('students.detail.credit.amountPlaceholder')} .value=${this.creditAmount} @input=${(e: CustomEvent) => (this.creditAmount = e.detail)}></cadence-input>
                <cadence-input label=${t('students.detail.credit.noteLabel')} placeholder=${t('students.detail.credit.notePlaceholder')} .value=${this.creditNote} @input=${(e: CustomEvent) => (this.creditNote = e.detail)}></cadence-input>
                <cadence-button @click=${() => this.issueCredit()}>${t('students.detail.credit.addCredit')}</cadence-button>
              </div>
            </cadence-card>
          </div>` : ''}
        ${this.tab === 'attendance' ? html`
          <div class="panel">
            ${this.attendance.length
              ? html`<cadence-table .columns=${[
                  { key: 'date', label: t('students.detail.attendance.colDate') }, { key: 'class_name', label: t('students.detail.attendance.colClass') },
                  { key: 'status', label: t('students.detail.attendance.colStatus'), render: (r: any) => html`<cadence-badge color=${this.attColor(r.status)}>${r.status}</cadence-badge>` },
                ]} .rows=${this.attendance}></cadence-table>`
              : html`<cadence-empty-state heading=${t('students.detail.attendance.empty')}></cadence-empty-state>`}
          </div>` : ''}
        ${moduleTabs.map((mt) => (this.tab === mt.id ? mt.render(this.studentId) : ''))}
      </div>

      ${this.renderGdpr(s)}
      ${this.recordingId ? this.renderRecordModal() : ''}
      ${this.showErase ? this.renderEraseModal(s) : ''}
    `;
  }

  private renderGdpr(s: any) {
    const canErase = store.hasPermission('students.delete');
    return html`
      <cadence-card class="gdpr" heading=${t('students.detail.gdpr.title')}>
        <p class="muted" style="margin-top:0">${t('students.detail.gdpr.lede')}</p>
        <div class="row">
          <cadence-button variant="secondary" ?disabled=${this.exporting} @click=${() => this.exportData()}>
            ${this.exporting ? t('students.detail.gdpr.exporting') : t('students.detail.gdpr.export')}
          </cadence-button>
          ${s.anonymized_at
            ? html`<span class="muted">${t('students.detail.gdpr.alreadyErased', { date: String(s.anonymized_at).slice(0, 10) })}</span>`
            : canErase
              ? html`<cadence-button variant="danger" @click=${() => { this.eraseConfirm = ''; this.showErase = true; }}>${t('students.detail.gdpr.erase')}</cadence-button>`
              : ''}
        </div>
      </cadence-card>`;
  }

  private renderEraseModal(s: any) {
    return html`
      <cadence-modal open heading=${t('students.detail.gdpr.eraseTitle')} @close=${() => (this.showErase = false)}>
        <p>${t('students.detail.gdpr.eraseWarning', { name: `${s.first_name ?? ''} ${s.last_name ?? ''}`.trim() })}</p>
        <ul class="muted" style="font-size:0.85rem;line-height:1.6">
          <li>${t('students.detail.gdpr.bulletAnonymize')}</li>
          <li>${t('students.detail.gdpr.bulletMedia')}</li>
          <li>${t('students.detail.gdpr.bulletFinancials')}</li>
        </ul>
        <cadence-input label=${t('students.detail.gdpr.confirmLabel')} placeholder="DELETE"
          .value=${this.eraseConfirm} @input=${(e: CustomEvent) => (this.eraseConfirm = e.detail)}></cadence-input>
        <div slot="footer">
          <cadence-button variant="ghost" @click=${() => (this.showErase = false)}>${t('common.actions.cancel')}</cadence-button>
          <cadence-button variant="danger" ?disabled=${this.eraseConfirm !== 'DELETE' || this.erasing} @click=${() => this.eraseData()}>
            ${this.erasing ? t('students.detail.gdpr.erasing') : t('students.detail.gdpr.eraseConfirmBtn')}
          </cadence-button>
        </div>
      </cadence-modal>`;
  }

  private renderRecordModal() {
    const p = this.payments.find((x) => x.id === this.recordingId);
    const enabled = this.methods.filter((m) => m.enabled);
    return html`
      <cadence-modal open heading=${t('students.detail.record.title')} @close=${() => this.closeRecord()}>
        ${p ? html`<p class="muted" style="margin-top:0">${p.fee_name ?? t('students.detail.credit.paymentDefault')} · <strong>${this.money(p.amount, p.currency)}</strong>${p.credit_applied ? html` <span class="muted">${t('students.detail.record.creditWillApply', { amount: this.money(p.credit_applied, p.currency) })}</span>` : ''}</p>` : ''}
        <div style="display:flex;flex-direction:column;gap:12px">
          <cadence-select label=${t('students.detail.record.paidVia')} .value=${this.recordMethodId}
            .options=${enabled.map((m) => ({ value: m.id, label: m.is_default ? t('students.detail.record.methodDefault', { name: m.name }) : m.name }))}
            @change=${(e: CustomEvent) => (this.recordMethodId = e.detail)}></cadence-select>
          <cadence-input label=${t('students.detail.record.referenceLabel')} placeholder=${t('students.detail.record.referencePlaceholder')} .value=${this.recordTxn} @input=${(e: CustomEvent) => (this.recordTxn = e.detail)}></cadence-input>
          <cadence-input label=${t('students.detail.record.notesLabel')} .value=${this.recordNotes} @input=${(e: CustomEvent) => (this.recordNotes = e.detail)}></cadence-input>
        </div>
        ${enabled.length ? '' : html`<p class="muted" style="color:var(--color-danger)">${t('students.detail.record.noMethods')}</p>`}
        <div slot="footer">
          <cadence-button variant="ghost" @click=${() => this.closeRecord()}>${t('common.actions.cancel')}</cadence-button>
          <cadence-button ?disabled=${!enabled.length} @click=${() => this.confirmRecord()}>${t('students.detail.record.title')}</cadence-button>
        </div>
      </cadence-modal>`;
  }
}
